import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Gift, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

interface ExitIntentPopupProps {
  enabled?: boolean;
}

export function ExitIntentPopup({ enabled = true }: ExitIntentPopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [hasShown, setHasShown] = useState(false);
  const isMobile = useIsMobile();
  
  // Mobile scroll-based exit intent detection
  const lastScrollY = useRef(0);
  const scrollVelocity = useRef(0);
  const lastScrollTime = useRef(Date.now());

  const checkCanShow = useCallback(() => {
    if (hasShown || !enabled) return false;
    
    // Check 24h cooldown
    const lastShown = localStorage.getItem('exitPopupLastShown');
    if (lastShown) {
      const hoursSince = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60);
      if (hoursSince < 24) return false;
    }
    
    // Don't show for logged in users
    const isLoggedIn = document.cookie.includes('sb-') || sessionStorage.getItem('supabase.auth.token');
    if (isLoggedIn) return false;
    
    return true;
  }, [hasShown, enabled]);

  const triggerPopup = useCallback(() => {
    if (!checkCanShow()) return;
    
    setIsOpen(true);
    setHasShown(true);
    localStorage.setItem('exitPopupLastShown', Date.now().toString());

    // Track no Pixel
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'ViewContent', { content_name: 'Exit Intent Popup' });
    }
  }, [checkCanShow]);

  // Desktop: Mouse exit detection
  const handleDesktopExitIntent = useCallback((e: MouseEvent) => {
    if (e.clientY <= 0) {
      triggerPopup();
    }
  }, [triggerPopup]);

  // Mobile: Fast scroll-up detection (user trying to leave)
  const handleMobileScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const currentTime = Date.now();
    const timeDelta = currentTime - lastScrollTime.current;
    
    if (timeDelta > 0) {
      const scrollDelta = lastScrollY.current - currentScrollY; // positive = scrolling up
      scrollVelocity.current = scrollDelta / timeDelta * 1000; // pixels per second
      
      // Trigger if:
      // 1. User is at top of page (< 100px from top)
      // 2. OR user is scrolling up very fast (> 2000px/s) from anywhere
      // 3. AND has scrolled down at least 500px before (engaged with content)
      const hasEngaged = lastScrollY.current > 500;
      const isFastScrollUp = scrollVelocity.current > 2000;
      const isNearTop = currentScrollY < 100 && lastScrollY.current > 300;
      
      if (hasEngaged && (isFastScrollUp || isNearTop)) {
        triggerPopup();
      }
    }
    
    lastScrollY.current = currentScrollY;
    lastScrollTime.current = currentTime;
  }, [triggerPopup]);

  useEffect(() => {
    if (!enabled) return;
    
    if (isMobile) {
      // Mobile: scroll-based detection
      window.addEventListener('scroll', handleMobileScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleMobileScroll);
    } else {
      // Desktop: mouse-out detection
      document.addEventListener('mouseout', handleDesktopExitIntent);
      return () => document.removeEventListener('mouseout', handleDesktopExitIntent);
    }
  }, [handleDesktopExitIntent, handleMobileScroll, enabled, isMobile]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    // Salva o email
    sessionStorage.setItem('signup_email', email);
    
    // Track Lead
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Lead', { email, source: 'exit_intent' });
    }

    toast.success('Pronto! Você receberá nosso conteúdo exclusivo.');
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Gift className="h-6 w-6 text-primary" />
            Espera! Temos um presente 🎁
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Antes de ir, deixe seu email e receba:
          </p>
          
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Planilha de controle de obras grátis</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>7 dias de teste premium</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Dicas semanais de gestão de obras</span>
            </li>
          </ul>
          
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <Input 
              type="email"
              placeholder="Seu melhor email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
              required
            />
            <Button type="submit" className="w-full h-11">
              Quero Receber
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
          
          <button 
            onClick={() => setIsOpen(false)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors w-full text-center"
          >
            Não, obrigado
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
